import React from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import ColumnHeader from "../../components/common/ColumnHeader.jsx";
import EngineeringSprintForm from "../../components/EngineeringSprintForm.jsx";
import FormattedDate from "../../components/FormattedDate.jsx";
import ReadableCurrency from "../../components/ReadableCurrency.jsx";
import PageContext from "../../contexts/PageContext.jsx";

const sortOptions = {
  defaultSort: {
    field: "endDate",
    reverse: true,
  },
};

function EngineeringProjectSprints({
  // Props
  project,
  sprints,
  mutate,
}) {
  const { editMode } = React.useContext(PageContext);
  const theme = useTheme();

  const [sortedSprints, handleSortSelection, sort] = useSort(sprints || [], sortOptions);

  return (
    <Box sx={{ mx: 2 }}>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="h6">Project Sprints</Typography>
            <EngineeringSprintForm project={project} mutate={mutate} data-cy="add-sprint-button" />
          </Stack>
        </Grid>
        <Grid size="auto">
          <IconButton disabled>
            <Icon />
          </IconButton>
        </Grid>
        <Grid size="grow">
          <ColumnHeader
            id="name"
            display="Sprint ID"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={3.5} sx={{ display: { xs: "none", sm: "block" } }}>
          <ColumnHeader
            id="endDate"
            display="End Date"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={2} sx={{ display: { xs: "none", sm: "block" } }}>
          <ColumnHeader
            id="hours"
            display="Hours"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={{ xs: 4, sm: 2 }}>
          <ColumnHeader
            id="cost"
            display="Cost"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
      </Grid>
      <Divider variant="middle" color={theme.palette.primary.dark} sx={{ borderWidth: "1px", mx: -1 }} />
      {sortedSprints.map((sprint) => (
        <React.Fragment key={sprint.id}>
          <Grid container spacing={1} sx={{ alignItems: "center" }}>
            <Grid size="auto">
              {editMode ? (
                <EngineeringSprintForm project={project} sprint={sprint} mutate={mutate} />
              ) : (
                <IconButton disabled>
                  <Icon />
                </IconButton>
              )}
            </Grid>
            <Grid size="grow">
              <Typography>{sprint.name}</Typography>
            </Grid>
            <Grid size={3.5} sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography>
                <FormattedDate dateString={sprint.endDate} />
              </Typography>
            </Grid>
            <Grid size={2} sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography>{sprint.hours}</Typography>
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <ReadableCurrency value={sprint.cost} />
            </Grid>
          </Grid>
          <Divider variant="middle" sx={{ mx: -1 }} />
        </React.Fragment>
      ))}
    </Box>
  );
}

export default EngineeringProjectSprints;
